import React, { useState } from 'react';
import {
    FileText,
    Scale,
    Shield,
    Users,
    Home,
    CreditCard,
    AlertTriangle,
    Mail,
    Phone,
    Calendar,
    ChevronDown,
    ChevronUp,
    ArrowLeft,
    CheckCircle,
    XCircle,
    Gavel,
    Building
} from 'lucide-react';

const TermsOfService = () => {
    const [expandedSection, setExpandedSection] = useState(null);

    const toggleSection = (sectionId) => {
        setExpandedSection(expandedSection === sectionId ? null : sectionId);
    };

    const lastUpdated = "June 18, 2025";
    const effectiveDate = "January 1, 2025";

    const sections = [
        {
            id: 'acceptance',
            title: 'Acceptance of Terms',
            icon: CheckCircle,
            content: (
                <div className="space-y-4">
                    <p className="text-soil-600">
                        By accessing or using the Rent Utopia platform, you agree to be bound by these Terms of Service
                        and all applicable laws and regulations. If you do not agree with any of these terms,
                        you are prohibited from using or accessing this site.
                    </p>

                    <div className="bg-highland-50 p-4 rounded-lg border border-highland-100">
                        <h4 className="font-semibold text-highland-700 mb-2">What Constitutes Acceptance</h4>
                        <ul className="text-highland-600 text-sm space-y-1">
                            <li>• Creating an account on our platform</li>
                            <li>• Browsing properties or listings</li>
                            <li>• Submitting rental applications</li>
                            <li>• Making payments through our system</li>
                            <li>• Communicating with other users</li>
                        </ul>
                    </div>

                    <div className="bg-murram-100/40 p-4 rounded-lg border border-murram-100">
                        <h4 className="font-semibold text-murram-600 mb-2">Age Requirement</h4>
                        <p className="text-murram-600 text-sm">
                            You must be at least 18 years old to use Rent Utopia. By using our services,
                            you represent and warrant that you are 18 years of age or older.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'platform-description',
            title: 'Platform Description',
            icon: Building,
            content: (
                <div className="space-y-4">
                    <p className="text-soil-600">
                        Rent Utopia is a digital platform that connects property seekers with property managers and landlords
                        in Kenya. We facilitate property listings, applications, and related services.
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-dawn-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-dawn-600 mb-2">For Tenants</h4>
                            <ul className="text-dawn-600 text-sm space-y-1">
                                <li>• Search and browse properties</li>
                                <li>• Submit rental applications</li>
                                <li>• Communicate with landlords</li>
                                <li>• Make payments online</li>
                                <li>• Access rental history</li>
                            </ul>
                        </div>

                        <div className="bg-jacaranda-100 p-4 rounded-lg">
                            <h4 className="font-semibold text-jacaranda-600 mb-2">For Property Managers</h4>
                            <ul className="text-jacaranda-600 text-sm space-y-1">
                                <li>• List properties for rent</li>
                                <li>• Manage applications</li>
                                <li>• Screen potential tenants</li>
                                <li>• Process payments</li>
                                <li>• Track property performance</li>
                            </ul>
                        </div>
                    </div>

                   {/* <div className="bg-soil-100 p-4 rounded-lg">
                        <h4 className="font-semibold text-soil-900 mb-2">Important Notice</h4>
                        <p className="text-soil-600 text-sm">
                            Rent Utopia is a technology platform that facilitates connections between users. We are not a
                            real estate agency, property management company, or landlord. We do not own, manage, or
                            control any properties listed on our platform.
                        </p>
                    </div>*/}
                </div>
            )
        },
        {
            id: 'user-accounts',
            title: 'User Accounts and Registration',
            icon: Users,
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-soil-900">Account Creation</h4>
                    <p className="text-soil-600">
                        To access certain features, you must register for an account by providing accurate and complete information.
                    </p>

                    <div className="space-y-3">
                        <div className="flex items-start space-x-3 p-3 bg-highland-50 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-highland-500 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-highland-700">Your Responsibilities</h4>
                                <ul className="text-highland-600 text-sm space-y-1 mt-1">
                                    <li>• Provide accurate and truthful information</li>
                                    <li>• Keep your account information up to date</li>
                                    <li>• Maintain the security of your password</li>
                                    <li>• Notify us immediately of unauthorized access</li>
                                </ul>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3 p-3 bg-murram-100/40 rounded-lg">
                            <XCircle className="w-5 h-5 text-murram-500 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-murram-600">Prohibited Actions</h4>
                                <ul className="text-murram-600 text-sm space-y-1 mt-1">
                                    <li>• Creating multiple accounts to circumvent restrictions</li>
                                    <li>• Sharing your account with others</li>
                                    <li>• Using false or misleading information</li>
                                    <li>• Impersonating another person or entity</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-murram-100/40 p-4 rounded-lg border border-murram-100">
                        <h4 className="font-semibold text-murram-600 mb-2">Account Verification</h4>
                        <p className="text-murram-600 text-sm">
                            We may require identity verification for certain services. This helps maintain the
                            security and integrity of our platform for all users.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'property-listings',
            title: 'Property Listings and Content',
            icon: Home,
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-soil-900">Listing Requirements</h4>
                    <p className="text-soil-600">
                        Property managers must ensure their listings are accurate, legal, and comply with all applicable laws.
                    </p>

                    <div className="space-y-4">
                        <div className="bg-dawn-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-dawn-600 mb-2">Required for All Listings</h4>
                            <ul className="text-dawn-600 text-sm space-y-1">
                                <li>• Accurate property description and amenities</li>
                                <li>• Current and truthful photographs</li>
                                <li>• Correct rental price and terms</li>
                                <li>• Valid contact information</li>
                                <li>• Legal right to rent the property</li>
                            </ul>
                        </div>

                        <div className="bg-murram-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-murram-600 mb-2">Prohibited Listings</h4>
                            <ul className="text-murram-600 text-sm space-y-1">
                                <li>• Properties you don't have legal authority to rent</li>
                                <li>• Discriminatory content or requirements</li>
                                <li>• False or misleading information</li>
                                <li>• Properties that violate local zoning laws</li>
                                <li>• Unsafe or uninhabitable properties</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-highland-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-highland-700 mb-2">Content Ownership and License</h4>
                        <p className="text-highland-600 text-sm">
                            You retain ownership of your content but grant Rent Utopia a license to use, display,
                            and distribute your listings on our platform for the purpose of providing our services.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'payments-fees',
            title: 'Payments and Fees',
            icon: CreditCard,
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-soil-900">Platform Fees</h4>
                    <p className="text-soil-600">
                        Rent Utopia charges fees for certain services. All fees are clearly disclosed before you complete a transaction.
                    </p>

                    <div className="grid gap-4">
                        <div className="bg-highland-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-highland-700 mb-2">For Tenants</h4>
                            <ul className="text-highland-600 text-sm space-y-1">
                                <li>• Basic platform use is free</li>
                                <li>• Application processing fee may apply</li>
                                <li>• Payment processing fees for online transactions</li>
                                <li>• Premium services (background checks, etc.)</li>
                            </ul>
                        </div>

                        <div className="bg-jacaranda-100 p-4 rounded-lg">
                            <h4 className="font-semibold text-jacaranda-600 mb-2">For Property Managers</h4>
                            <ul className="text-jacaranda-600 text-sm space-y-1">
                                <li>• Commission on successful rentals</li>
                                <li>• Featured listing upgrades</li>
                                <li>• Additional photography services</li>
                                <li>• Premium analytics and reporting</li>
                            </ul>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="bg-dawn-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-dawn-600 mb-2">Payment Terms</h4>
                            <ul className="text-dawn-600 text-sm space-y-1">
                                <li>• All fees are due immediately upon service completion</li>
                                <li>• Payments are processed through secure third-party processors</li>
                                <li>• Refunds are subject to our refund policy</li>
                                <li>• Failed payments may result in service suspension</li>
                            </ul>
                        </div>

                        <div className="bg-murram-100/40 p-4 rounded-lg border border-murram-100">
                            <h4 className="font-semibold text-murram-600 mb-2">Currency and Taxes</h4>
                            <p className="text-murram-600 text-sm">
                                All fees are quoted in Kenyan Shillings (KES). Applicable taxes will be added to your bill.
                                You are responsible for any taxes or duties in your jurisdiction.
                            </p>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'user-conduct',
            title: 'User Conduct and Prohibited Activities',
            icon: Shield,
            content: (
                <div className="space-y-4">
                    <p className="text-soil-600">
                        Users must conduct themselves professionally and lawfully when using Rent Utopia.
                    </p>

                    <div className="space-y-4">
                        <div className="bg-murram-100/40 p-4 rounded-lg border border-murram-100">
                            <h4 className="font-semibold text-murram-600 mb-2">Strictly Prohibited</h4>
                            <ul className="text-murram-600 text-sm space-y-1">
                                <li>• Harassment, threats, or abusive behavior</li>
                                <li>• Discrimination based on race, religion, gender, etc.</li>
                                <li>• Fraudulent or deceptive practices</li>
                                <li>• Violation of intellectual property rights</li>
                                <li>• Spam or unsolicited communications</li>
                                <li>• Attempting to circumvent platform fees</li>
                                <li>• Using the platform for illegal activities</li>
                            </ul>
                        </div>

                        <div className="bg-dawn-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-dawn-600 mb-2">Expected Behavior</h4>
                            <ul className="text-dawn-600 text-sm space-y-1">
                                <li>• Treat all users with respect and professionalism</li>
                                <li>• Provide accurate and honest information</li>
                                <li>• Respond to communications in a timely manner</li>
                                <li>• Comply with all applicable laws and regulations</li>
                                <li>• Report suspicious or inappropriate behavior</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-murram-100/40 p-4 rounded-lg border border-murram-100">
                        <h4 className="font-semibold text-murram-600 mb-2">Enforcement</h4>
                        <p className="text-murram-600 text-sm">
                            Violations may result in warnings, account suspension, or permanent ban from the platform.
                            We reserve the right to take appropriate action to maintain platform safety and integrity.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'platform-availability',
            title: 'Platform Availability and Modifications',
            icon: AlertTriangle,
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-soil-900">Service Availability</h4>
                    <p className="text-soil-600">
                        While we strive to maintain continuous service, Rent Utopia may be unavailable due to maintenance,
                        technical issues, or other factors beyond our control.
                    </p>

                    <div className="space-y-3">
                        <div className="bg-highland-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-highland-700 mb-2">Planned Maintenance</h4>
                            <p className="text-highland-600 text-sm">
                                We will provide advance notice for scheduled maintenance whenever possible.
                                Emergency maintenance may occur without prior notice.
                            </p>
                        </div>

                        <div className="bg-murram-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-murram-600 mb-2">Service Modifications</h4>
                            <p className="text-murram-600 text-sm">
                                We reserve the right to modify, suspend, or discontinue any aspect of our service
                                at any time. We will provide reasonable notice for significant changes.
                            </p>
                        </div>

                        <div className="bg-soil-100 p-4 rounded-lg">
                            <h4 className="font-semibold text-soil-900 mb-2">No Warranty</h4>
                            <p className="text-soil-600 text-sm">
                                Rent Utopia is provided "as is" without warranties of any kind. We do not guarantee
                                uninterrupted access or that the service will be error-free.
                            </p>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'liability-disclaimers',
            title: 'Liability and Disclaimers',
            icon: Scale,
            content: (
                <div className="space-y-4">
                    <div className="bg-murram-100/40 p-4 rounded-lg border border-murram-100">
                        <h4 className="font-semibold text-murram-600 mb-2">Important Disclaimer</h4>
                        <p className="text-murram-600 text-sm">
                            Rent Utopia is a platform that facilitates connections between users. We are not responsible
                            for the actions, representations, or omissions of users or third parties.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-soil-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-soil-900 mb-2">Limitation of Liability</h4>
                            <ul className="text-soil-600 text-sm space-y-1">
                                <li>• We are not liable for property condition or habitability</li>
                                <li>• We do not guarantee the accuracy of user-provided information</li>
                                <li>• We are not responsible for disputes between users</li>
                                <li>• Our liability is limited to the fees paid to us in the past 12 months</li>
                            </ul>
                        </div>

                        <div className="bg-highland-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-highland-700 mb-2">User Responsibility</h4>
                            <ul className="text-highland-600 text-sm space-y-1">
                                <li>• Verify property information independently</li>
                                <li>• Conduct due diligence on other users</li>
                                <li>• Ensure compliance with local laws and regulations</li>
                                <li>• Maintain appropriate insurance coverage</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-murram-100/40 p-4 rounded-lg border border-murram-100">
                        <h4 className="font-semibold text-murram-600 mb-2">Indemnification</h4>
                        <p className="text-murram-600 text-sm">
                            You agree to indemnify and hold Rent Utopia harmless from any claims, damages, or expenses
                            arising from your use of the platform or violation of these terms.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'termination',
            title: 'Termination',
            icon: XCircle,
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-soil-900">Account Termination</h4>
                    <p className="text-soil-600">
                        Either party may terminate the relationship under certain circumstances.
                    </p>

                    <div className="space-y-3">
                        <div className="bg-highland-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-highland-700 mb-2">Termination by You</h4>
                            <ul className="text-highland-600 text-sm space-y-1">
                                <li>• You may close your account at any time</li>
                                <li>• Contact our support team to initiate closure</li>
                                <li>• Some information may be retained as required by law</li>
                                <li>• Outstanding obligations remain in effect</li>
                            </ul>
                        </div>

                        <div className="bg-murram-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-murram-600 mb-2">Termination by Rent Utopia</h4>
                            <ul className="text-murram-600 text-sm space-y-1">
                                <li>• Violation of these terms of service</li>
                                <li>• Fraudulent or illegal activity</li>
                                <li>• Abuse of other users or platform</li>
                                <li>• Non-payment of fees</li>
                                <li>• At our discretion with notice</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-soil-100 p-4 rounded-lg">
                        <h4 className="font-semibold text-soil-900 mb-2">Effect of Termination</h4>
                        <p className="text-soil-600 text-sm">
                            Upon termination, your access to the platform will cease, but these terms will continue
                            to apply to any past use of the service. Certain provisions will survive termination.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'governing-law',
            title: 'Governing Law and Disputes',
            icon: Gavel,
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-soil-900">Applicable Law</h4>
                    <p className="text-soil-600">
                        These terms are governed by the laws of Kenya, and any disputes will be resolved in Kenyan courts.
                    </p>

                    <div className="space-y-4">
                        <div className="bg-highland-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-highland-700 mb-2">Dispute Resolution Process</h4>
                            <ol className="text-highland-600 text-sm space-y-1 list-decimal list-inside">
                                <li>Contact our support team to resolve informally</li>
                                <li>Mediation through agreed-upon mediator</li>
                                <li>Arbitration if mediation fails</li>
                                <li>Court proceedings as last resort</li>
                            </ol>
                        </div>

                        <div className="bg-dawn-100/40 p-4 rounded-lg">
                            <h4 className="font-semibold text-dawn-600 mb-2">Jurisdiction</h4>
                            <p className="text-dawn-600 text-sm">
                                Any legal proceedings must be brought in the courts of Nairobi, Kenya.
                                You consent to the jurisdiction of these courts.
                            </p>
                        </div>
                    </div>

                    <div className="bg-murram-100/40 p-4 rounded-lg border border-murram-100">
                        <h4 className="font-semibold text-murram-600 mb-2">Class Action Waiver</h4>
                        <p className="text-murram-600 text-sm">
                            You agree to resolve disputes individually and waive the right to participate
                            in class action lawsuits or similar collective proceedings.
                        </p>
                    </div>
                </div>
            )
        }
    ];

    const CollapsibleSection = ({ section }) => {
        const Icon = section.icon;
        const isExpanded = expandedSection === section.id;

        return (
            <div className="border border-soil-100 rounded-lg overflow-hidden">
                <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full px-6 py-4 text-left bg-white hover:bg-soil-50 transition-colors duration-200 flex items-center justify-between"
                >
                    <div className="flex items-center space-x-3">
                        <Icon className="w-5 h-5 text-highland-500" />
                        <h3 className="text-lg font-semibold text-soil-900">{section.title}</h3>
                    </div>
                    {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-soil-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-soil-400" />
                    )}
                </button>

                {isExpanded && (
                    <div className="px-6 py-4 bg-soil-50 border-t border-soil-100">
                        {section.content}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-karibu-100">
            {/* Header */}
            <div className="bg-white shadow-sm">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="p-3 bg-highland-50 rounded-full">
                                <FileText className="w-8 h-8 text-highland-500" />
                            </div>
                        </div>
                        <h1 className="font-display text-3xl font-bold text-soil-900 mb-2">Terms of Service</h1>
                        <p className="text-soil-600 max-w-2xl mx-auto">
                            These terms govern your use of Rent Utopia's platform and services. Please read them carefully
                            as they contain important information about your rights and obligations.
                        </p>
                        <div className="flex items-center justify-center mt-4 space-x-6 text-sm text-soil-400">
                            <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                <span>Last updated: {lastUpdated}</span>
                            </div>
                            <div className="flex items-center">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                <span>Effective: {effectiveDate}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center space-x-2 text-sm text-soil-600">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                    </button>
                    <span>/</span>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <Home className="w-4 h-4 mr-1" />
                        Home
                    </button>
                    <span>/</span>
                    <span className="text-soil-900">Terms of Service</span>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                {/* Introduction */}
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                    <h2 className="text-xl font-semibold text-soil-900 mb-4">Welcome to Rent Utopia</h2>
                    <p className="text-soil-600 mb-4">
                        Rent Utopia Limited ("we," "us," or "our") operates a digital platform that connects property
                        seekers with property managers and landlords in Kenya. These Terms of Service ("Terms")
                        constitute a legally binding agreement between you and Rent Utopia.
                    </p>
                    <div className="bg-highland-50 p-4 rounded-lg border border-highland-100">
                        <h3 className="font-semibold text-highland-700 mb-2">Key Points</h3>
                        <ul className="text-highland-600 text-sm space-y-1">
                            <li>• These terms apply to all users of our platform</li>
                            <li>• By using Rent Utopia, you agree to these terms</li>
                            <li>• We may update these terms from time to time</li>
                            <li>• Continued use after changes constitutes acceptance</li>
                        </ul>
                    </div>
                </div>

                {/* Collapsible Sections */}
                <div className="space-y-4 mb-8">
                    {sections.map((section) => (
                        <CollapsibleSection key={section.id} section={section} />
                    ))}
                </div>

                {/* Updates and Changes */}
                <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
                    <h2 className="text-xl font-semibold text-soil-900 mb-4">Changes to Terms</h2>
                    <p className="text-soil-600 mb-4">
                        We reserve the right to modify these terms at any time. We will notify users of significant
                        changes through email or platform notifications.
                    </p>
                    <div className="bg-murram-100/40 p-4 rounded-lg border border-murram-100">
                        <h3 className="font-semibold text-murram-600 mb-2">Your Options</h3>
                        <p className="text-murram-600 text-sm">
                            If you disagree with any changes, you may terminate your account before the changes take effect.
                            Continued use of the platform after changes become effective constitutes acceptance of the new terms.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsOfService;